import api from './api';
import { resolveSectionDefinitions } from '../utils/aiPromptConfigUtils';
import { DEFAULT_SECTION_KEYS, humanizeSectionKey } from '../utils/rfpGeneratedContentUtils';

const SECTION_TIMEOUT_MS = 120000;

const resolveSectionOrder = (promptConfig) => {
  const defs = resolveSectionDefinitions(promptConfig);
  if (defs.length) return defs.map((d) => d.key);
  return [...DEFAULT_SECTION_KEYS];
};

class AiRfpService {
  static async createDraft(inputPayload, promptConfig = null) {
    const response = await api.post(
      '/rfp/create',
      {
        inputPayload,
        promptConfig,
      },
      { timeout: 180000 },
    );
    return response.data;
  }

  static async generateSection({
    draftId,
    inputPayload,
    promptConfig = null,
    sectionKey,
    sectionIndex,
    totalSections,
  }) {
    const response = await api.post(
      '/rfp/generate-section',
      {
        draftId,
        inputPayload,
        promptConfig,
        sectionKey,
        sectionIndex,
        totalSections,
      },
      { timeout: SECTION_TIMEOUT_MS },
    );
    return response.data;
  }

  static async generateRfpBySections({
    draftId,
    inputPayload,
    promptConfig = null,
    onProgress,
  }) {
    const sectionOrder = resolveSectionOrder(promptConfig);
    let currentDraftId = draftId || null;
    let generatedContent = {};
    let lastError = null;

    for (let i = 0; i < sectionOrder.length; i += 1) {
      const sectionKey = sectionOrder[i];
      onProgress?.({
        currentIndex: i,
        totalSections: sectionOrder.length,
        currentSectionKey: sectionKey,
        currentSectionLabel: humanizeSectionKey(sectionKey),
        completedSections: i,
        percent: Math.round((i / sectionOrder.length) * 100),
        status: 'generating',
        failedSections: [],
      });

      try {
        const response = await this.generateSection({
          draftId: currentDraftId,
          inputPayload,
          promptConfig,
          sectionKey,
          sectionIndex: i,
          totalSections: sectionOrder.length,
        });

        currentDraftId = response?.data?.draftId || currentDraftId;
        generatedContent = response?.data?.generatedContent || generatedContent;
      } catch (err) {
        lastError = err;
        onProgress?.({
          currentIndex: i,
          totalSections: sectionOrder.length,
          currentSectionKey: sectionKey,
          currentSectionLabel: humanizeSectionKey(sectionKey),
          completedSections: i,
          percent: Math.round((i / sectionOrder.length) * 100),
          status: 'section_failed',
          failedSections: [sectionKey],
          errorMessage:
            err?.response?.data?.message ||
            err?.message ||
            `Failed to generate ${humanizeSectionKey(sectionKey)}`,
        });
        throw err;
      }

      onProgress?.({
        currentIndex: i + 1,
        totalSections: sectionOrder.length,
        currentSectionKey: sectionKey,
        currentSectionLabel: humanizeSectionKey(sectionKey),
        completedSections: i + 1,
        percent: Math.round(((i + 1) / sectionOrder.length) * 100),
        status: i + 1 >= sectionOrder.length ? 'complete' : 'generating',
        failedSections: [],
      });
    }

    if (!generatedContent || !Object.keys(generatedContent).length) {
      throw lastError || new Error('Generated AI content is empty.');
    }

    return {
      draftId: currentDraftId,
      generatedContent,
      sectionOrder,
    };
  }
}

export default AiRfpService;
